import { useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Loader2, Search } from 'lucide-react';
import { effectiveClientId, isStaff, useAuthClaims } from '../hooks/useAuthClaims';
import { listClientsForStaff, type ClientListItem } from '../lib/firestoreClient';

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { loading, user, claims } = useAuthClaims();
  const [search, setSearch] = useSearchParams();
  const previewParam = search.get('as');

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[color:var(--color-brand-primary)]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const { clientId } = effectiveClientId(claims, previewParam);

  // Staff signed in without ?as= → let them pick a client instead of making
  // them hand-edit the URL. Setting the param this way keeps whatever path
  // they arrived on, so a staff link to /report/<slug> still lands on that
  // report once a client is chosen.
  if (isStaff(claims) && !clientId) {
    return (
      <StaffClientPicker
        role={claims.role as 'admin' | 'project_coordinator'}
        assignedClientIds={claims.assignedClientIds}
        onPick={(id) =>
          setSearch(
            (prev) => {
              prev.set('as', id);
              return prev;
            },
            { replace: true },
          )
        }
      />
    );
  }

  if (!clientId) {
    return (
      <div className="mx-auto max-w-md p-10 text-center">
        <h1 className="typo-heading-sm text-[color:var(--color-text-primary)]">Account not ready</h1>
        <p className="mt-2 text-sm text-[color:var(--color-text-tertiary)]">
          Your account isn’t linked to a client yet. Please contact your Reputation X project manager.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

function StaffClientPicker({
  role,
  assignedClientIds,
  onPick,
}: {
  role: 'admin' | 'project_coordinator';
  assignedClientIds: string[];
  onPick: (clientId: string) => void;
}) {
  const [clients, setClients] = useState<ClientListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  // Join the ids so a fresh-but-equal array from a claims refresh doesn't
  // retrigger the fetch.
  const assignedKey = assignedClientIds.join(',');

  useEffect(() => {
    let cancelled = false;
    setClients(null);
    setError(null);
    listClientsForStaff(role, assignedKey ? assignedKey.split(',') : [])
      .then((rows) => {
        if (!cancelled) setClients(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load clients.');
      });
    return () => {
      cancelled = true;
    };
  }, [role, assignedKey]);

  const visible = useMemo(() => {
    if (!clients) return [];
    const needle = filter.trim().toLowerCase();
    if (!needle) return clients;
    return clients.filter(
      (c) => c.name.toLowerCase().includes(needle) || c.id.toLowerCase().includes(needle),
    );
  }, [clients, filter]);

  return (
    <div className="mx-auto max-w-lg px-6 py-12">
      <h1 className="typo-heading-sm text-[color:var(--color-text-primary)]">
        Choose a client to preview
      </h1>
      <p className="mt-1.5 text-sm text-[color:var(--color-text-tertiary)]">
        You’re signed in as staff. Pick a client to see the portal exactly as they would.
      </p>

      {clients && clients.length > 8 && (
        <div className="relative mt-5">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--color-text-placeholder)]" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search clients"
            autoFocus
            className="w-full rounded-lg border py-2 pl-9 pr-3 text-sm outline-none focus:ring-2"
            style={{
              borderColor: 'var(--color-border)',
              background: 'var(--color-surface-white)',
              color: 'var(--color-text-primary)',
            }}
          />
        </div>
      )}

      <div className="mt-5">
        {error && (
          <div
            className="rounded-lg border p-4 text-sm"
            style={{
              borderColor: 'var(--color-accent-coral)',
              background: 'var(--color-status-danger)',
              color: 'var(--color-ink-danger)',
            }}
          >
            {error}
          </div>
        )}

        {!error && !clients && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-[color:var(--color-brand-primary)]" />
          </div>
        )}

        {!error && clients && clients.length === 0 && (
          <p className="py-8 text-center text-sm text-[color:var(--color-text-tertiary)]">
            {role === 'project_coordinator'
              ? 'No clients are assigned to you yet. Ask an admin to assign you a client.'
              : 'No clients exist yet.'}
          </p>
        )}

        {!error && clients && clients.length > 0 && visible.length === 0 && (
          <p className="py-8 text-center text-sm text-[color:var(--color-text-tertiary)]">
            No clients match “{filter.trim()}”.
          </p>
        )}

        {visible.length > 0 && (
          <ul
            className="divide-y overflow-hidden rounded-lg border"
            style={{
              borderColor: 'var(--color-border)',
              background: 'var(--color-surface-white)',
            }}
          >
            {visible.map((c) => (
              <li key={c.id} style={{ borderColor: 'var(--color-border)' }}>
                <button
                  type="button"
                  onClick={() => onPick(c.id)}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-[color:var(--color-surface-light)]"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-[color:var(--color-text-primary)]">
                      {c.name}
                    </span>
                    <span className="block truncate font-mono text-xs text-[color:var(--color-text-tertiary)]">
                      {c.id}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-[color:var(--color-text-tertiary)]" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
